import React, { useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { usePermissions } from '../hooks/usePermissions';
import { useGetClientsQuery, useGetAllClientsQuery } from '../store/api/clientsApi';
import { useGetUserProfileQuery } from '../store/api/userApi';
import { useGetProvidersQuery } from '../store/api/providersApi';
import { Button, Card, Input, Select } from '../styles/components';
import { CreateClientWizard } from '../components/clients/CreateClientWizard';
import { ClientDetailModal } from '../components/clients/ClientDetailModal';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const STATUS_MAP: Record<string, string> = {
  '0': 'Created',
  '2': 'Active',
  '3': 'Locked',
  '4': 'ActivationFailed',
  '5': 'Deleted',
  'Created': 'Created',
  'Active': 'Active',
  'Locked': 'Locked',
  'ActivationFailed': 'ActivationFailed',
  'Deleted': 'Deleted',
};

const ClientsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
  max-width: 1200px;
  margin: 2rem auto;
  width: 100%;
  padding: 0 2rem;
`;

const ClientsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderContent = styled.div``;

const ClientsTitle = styled.h1`
  font-size: ${props => props.theme.fontSizes['2xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.gray[900]};
`;

const ClientsSubtitle = styled.p`
  margin-top: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.gray[500]};
`;

const CreateButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  
  svg {
    height: 1rem;
    width: 1rem;
  }
`;

const ClientsTable = styled(Card)`
  padding: 0;
`;

const Table = styled.table`
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
`;

const TableHead = styled.thead`
  background-color: ${props => props.theme.colors.gray[50]};
`;

const TableHeader = styled.th`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.xl};
  text-align: left;
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.gray[500]};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SortableTableHeader = styled(TableHeader)`
  cursor: pointer;
  user-select: none;
  white-space: nowrap;

  &:hover {
    color: ${props => props.theme.colors.gray[700]};
  }
`;

const TableBody = styled.tbody`
  background-color: white;
`;

const TableRow = styled.tr<{ $isDeleted?: boolean }>`
  border-top: 1px solid ${props => props.theme.colors.gray[200]};
  opacity: ${props => props.$isDeleted ? 0.5 : 1};

  &:hover {
    background-color: ${props => props.$isDeleted ? 'transparent' : props.theme.colors.gray[50]};
  }
`;

const TableCell = styled.td`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  white-space: nowrap;
`;

const ClientId = styled.div<{ $isDeleted?: boolean }>`
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.$isDeleted ? props.theme.colors.gray[500] : props.theme.colors.primary[600]};
  cursor: ${props => props.$isDeleted ? 'not-allowed' : 'pointer'};
  pointer-events: ${props => props.$isDeleted ? 'none' : 'auto'};

  &:hover {
    color: ${props => props.$isDeleted ? props.theme.colors.gray[500] : props.theme.colors.primary[900]};
    text-decoration: ${props => props.$isDeleted ? 'none' : 'underline'};
  }
`;

const ClientIdSub = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[500]};
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  border-radius: ${props => props.theme.borders.radius.sm};

  ${props => {
    switch (props.status) {
      case 'Active':
        return `
          background-color: ${props.theme.colors.success[50]};
          color: ${props.theme.colors.success[600]};
        `;
      case 'Locked':
        return `
          background-color: ${props.theme.colors.error[50]};
          color: ${props.theme.colors.error[600]};
        `;
      case 'Created':
        return `
          background-color: ${props.theme.colors.warning[50]};
          color: ${props.theme.colors.warning[600]};
        `;
      case 'Deleted':
        return `
          background-color: ${props.theme.colors.gray[100]};
          color: ${props.theme.colors.gray[500]};
        `;
      default:
        return `
          background-color: ${props.theme.colors.gray[50]};
          color: ${props.theme.colors.gray[600]};
        `;
    }
  }}
`;

const LoadingCard = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 16rem;
  color: ${props => props.theme.colors.gray[500]};
`;

const EmptyCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing['2xl']};
  text-align: center;
  color: ${props => props.theme.colors.gray[500]};
`;

const HelpBanner = styled(Card)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.primary[50]};
  border: 1px solid ${props => props.theme.colors.primary[200]};
  margin-bottom: ${props => props.theme.spacing.xl};

  svg {
    flex-shrink: 0;
    width: 1.5rem;
    height: 1.5rem;
    color: ${props => props.theme.colors.primary[600]};
  }
`;

const HelpBannerContent = styled.div`
  flex: 1;
`;

const HelpBannerTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.base};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.gray[900]};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const HelpBannerText = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[600]};
`;

const HelpButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  white-space: nowrap;

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  
  svg {
    position: absolute;
    left: ${props => props.theme.spacing.sm};
    top: 50%;
    transform: translateY(-50%);
    height: 1.25rem;
    width: 1.25rem;
    color: ${props => props.theme.colors.gray[400]};
  }
`;

const SearchInput = styled(Input)`
  padding-left: ${props => props.theme.spacing.xl};
  width: 100%;

  @media (min-width: 768px) {
    width: 24rem;
  }
`;

const FilterSelect = styled(Select)`
  width: 100%;
  
  @media (min-width: 768px) {
    width: 10rem;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: between;
  margin-top: ${props => props.theme.spacing.xl};
  padding: ${props => props.theme.spacing.md};
  background: white;
  border-radius: ${props => props.theme.borders.radius.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
`;

const PaginationInfo = styled.div`
  flex: 1;
  color: ${props => props.theme.colors.gray[500]};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const PageButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  min-width: 2.5rem;
  height: 2.5rem;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  border-radius: ${props => props.theme.borders.radius.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  background: ${props => props.$active ? props.theme.colors.primary[50] : 'white'};
  color: ${props => props.$active ? props.theme.colors.primary[600] : props.theme.colors.gray[500]};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$active ? props.theme.colors.primary[100] : props.theme.colors.gray[50]};
    border-color: ${props => props.$active ? props.theme.colors.primary[300] : props.theme.colors.gray[300]};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    height: 1.25rem;
    width: 1.25rem;
  }
`;

const ResultsCount = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.gray[500]};
  font-size: ${props => props.theme.fontSizes.sm};

  svg {
    height: 1rem;
    width: 1rem;
  }
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.gray[700]};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  padding: ${props => props.theme.spacing.xs};
  border-radius: ${props => props.theme.borders.radius.md};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.colors.gray[50]};
  }

  input[type="checkbox"] {
    width: 1.125rem;
    height: 1.125rem;
    cursor: pointer;
    accent-color: ${props => props.theme.colors.primary[600]};
  }
`;

export function Clients() {
  const navigate = useNavigate();
  const { isSupport, isApiUser } = usePermissions();
  const { data: userProfile } = useGetUserProfileQuery();
  const { data: providers } = useGetProvidersQuery();
  const authUser = useSelector((state: RootState) => state.auth.user);
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    search: '',
    status: undefined as string | undefined,
    providerId: undefined as string | undefined,
    includeDeleted: false,
  });
  const [sort, setSort] = useState<{ sortBy: string | undefined; sortDesc: boolean }>({
    sortBy: undefined,
    sortDesc: false,
  });

  const handleSortToggle = (field: string) => {
    setSort(prev =>
      prev.sortBy === field
        ? { sortBy: field, sortDesc: !prev.sortDesc }
        : { sortBy: field, sortDesc: false }
    );
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPage(1);
  };

  const handleStatusChange = (value: string) => {
    setFilters(prev => ({ ...prev, status: value || undefined }));
    setPage(1);
  };

  const handleProviderChange = (value: string) => {
    setFilters(prev => ({ ...prev, providerId: value || undefined }));
    setPage(1);
  };

  const handleIncludeDeletedChange = (checked: boolean) => {
    setFilters(prev => ({ ...prev, includeDeleted: checked }));
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const queryParams = {
    page,
    pageSize: 20,
    ...filters,
    ...sort,
  };

  const { data: userClients, isLoading: userClientsLoading } = useGetClientsQuery(queryParams, {
    skip: isSupport,
  });

  const { data: allClients, isLoading: allClientsLoading } = useGetAllClientsQuery(queryParams, {
    skip: !isSupport,
  });

  const data = isSupport ? allClients : userClients;
  const isLoading = isSupport ? allClientsLoading : userClientsLoading;

  // Check if API user has clients
  const hasClients = authUser?.hasClients ?? userProfile?.hasClients ?? true;

  if (isLoading) {
    return (
      <ClientsContainer>
        <LoadingCard>Loading clients...</LoadingCard>
      </ClientsContainer>
    );
  }

  return (
    <ClientsContainer>
      {/* Show help banner for API users without clients */}
      {isApiUser && !hasClients && (
        <HelpBanner>
          <InformationCircleIcon />
          <HelpBannerContent>
            <HelpBannerTitle>Get Started with FinTra</HelpBannerTitle>
            <HelpBannerText>
              Create your first API client to start integrating with the Financial Transactions Gateway. Visit the Help page for detailed documentation and guides.
            </HelpBannerText>
          </HelpBannerContent>
          <HelpButton variant="outline" onClick={() => navigate('/help')}>
            View Help
            <ArrowRightIcon />
          </HelpButton>
        </HelpBanner>
      )}

      <ClientsHeader>
        <HeaderContent>
          <ClientsTitle>
            Clients
          </ClientsTitle>
          <ClientsSubtitle>
            {isSupport 
              ? 'Manage all client configurations across organizations'
              : 'Manage your organizations clients used to interact with the banks'
            }
          </ClientsSubtitle>
        </HeaderContent>
        <CreateButton onClick={() => setShowCreateModal(true)}>
          <PlusIcon />
          Create Client
        </CreateButton>
      </ClientsHeader>

      <FilterContainer>
        <FilterGroup>
          <SearchContainer>
            <MagnifyingGlassIcon />
            <SearchInput
              placeholder="Search"
              value={filters.search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </SearchContainer>
          <FilterSelect
            value={filters.status || ''}
            onChange={(e) => handleStatusChange(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="Created">Created</option>
            <option value="Active">Active</option>
            <option value="Locked">Locked</option>
            <option value="ActivationFailed">Activation Failed</option>
          </FilterSelect>
          <FilterSelect
            value={filters.providerId || ''}
            onChange={(e) => handleProviderChange(e.target.value)}
          >
            <option value="">All Institutions</option>
            {(providers || []).map((provider) => (
              <option key={provider.id} value={provider.id}>
                {provider.name}
              </option>
            ))}
          </FilterSelect>
          <CheckboxContainer>
            <input
              type="checkbox"
              checked={filters.includeDeleted}
              onChange={(e) => handleIncludeDeletedChange(e.target.checked)}
            />
            Show deleted clients
          </CheckboxContainer>
        </FilterGroup>

        <ResultsCount>
          <FunnelIcon />
          {data?.totalCount || 0} clients
        </ResultsCount>
      </FilterContainer>
      
      {!data?.items.length ? (
        <EmptyCard>
          <p>No clients found</p>
        </EmptyCard>
      ) : (
        <>
          <ClientsTable>
          <Table>
            <TableHead>
              <tr>
                <TableHeader>Client</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Provider</TableHeader>
                <TableHeader>Organization</TableHeader>
                <TableHeader>Certificate Serial Number</TableHeader>
                <SortableTableHeader onClick={() => handleSortToggle('certificateExpirationDate')}>
                  Certificate Expiration Date{' '}
                  {sort.sortBy === 'certificateExpirationDate' ? (sort.sortDesc ? '↓' : '↑') : '↕'}
                </SortableTableHeader>
                <SortableTableHeader onClick={() => handleSortToggle('created')}>
                  Created{' '}
                  {sort.sortBy === 'created' ? (sort.sortDesc ? '↓' : '↑') : '↕'}
                </SortableTableHeader>
              </tr>
            </TableHead>
            <TableBody>
              {data.items.map((client) => {
                const statusString = STATUS_MAP[client.status] || client.status;
                const isDeleted = statusString === 'Deleted';
                return (
                  <TableRow key={client.id} $isDeleted={isDeleted}>
                    <TableCell>
                      <ClientId
                        $isDeleted={isDeleted}
                        onClick={() => !isDeleted && setSelectedClientId(client.clientId)}
                      >
                        {client.clientId}
                      </ClientId>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={STATUS_MAP[client.status] || client.status}>
                        {STATUS_MAP[client.status] || client.status}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      {client.providerName || '-'}
                    </TableCell>
                    <TableCell>
                      <div>{client.organizationName || '-'}</div>
                      <ClientIdSub>{client.organizationId || '-'}</ClientIdSub>
                    </TableCell>
                    <TableCell>
                      {client.certificateSerialNumber || '-'}
                    </TableCell>
                    <TableCell>
                      {client.certificateExpirationDate
                        ? new Date(client.certificateExpirationDate).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {new Date(client.created).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </ClientsTable>
          
          <PaginationContainer>
            <PaginationInfo>
              Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, data.totalCount)} of {data.totalCount} results
            </PaginationInfo>
            
            <PaginationControls>
              <PageButton
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                <ChevronLeftIcon />
              </PageButton>
              
              {Array.from({ length: Math.min(5, data.totalPages) }, (_, i) => {
                const pageNumber = Math.max(1, page - 2) + i;
                if (pageNumber > data.totalPages) return null;
                
                return (
                  <PageButton
                    key={pageNumber}
                    $active={pageNumber === page}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </PageButton>
                );
              })}
              
              <PageButton
                onClick={() => handlePageChange(page + 1)}
                disabled={page === data.totalPages}
              >
                <ChevronRightIcon />
              </PageButton>
            </PaginationControls>
          </PaginationContainer>
        </>
      )}

      {showCreateModal && (
        <CreateClientWizard
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            // Refetch will happen automatically due to cache invalidation
          }}
        />
      )}
      
      {selectedClientId && (
        <ClientDetailModal
          clientId={selectedClientId}
          onClose={() => setSelectedClientId(null)}
        />
      )}
    </ClientsContainer>
  );
}